import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Title, Tooltip } from 'chart.js';
import PropTypes from 'prop-types';
import { Bar } from 'react-chartjs-2';
import dayjs from '../../utils/dayjs';
import { getAnalyticsSeriesColors, getSemanticSeriesColor, withAlpha } from './analyticsColors';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const monthStrLen = 'short';

export function StackedBarChart({ chartData, title, query, type, showTitle = true, paletteVariant = 'default' }) {
  let dates = parseDateRangeFromQuery(query);
  let { startDate, endDate } = dates;
  let monthDateArray = getMonthDatesArray(startDate, endDate);
  let placeholderName = 'empty month placeholder';

  const data = {
    datasets: [],
  };

  //add placeholder values to show empty months
  data.datasets.push({
    label: placeholderName,
    data: getMonthsWithInitialValue1(monthDateArray),
    borderColor: 'black',
    backgroundColor: 'white',
    borderWidth: 1,
    borderRadius: 3,
    hoverBackgroundColor: 'white',
  });

  const dataProcessingDoneInMongoQuery = chartData[0].hasOwnProperty('label');

  if (dataProcessingDoneInMongoQuery) {
    chartData.forEach((d, index) => {
      const semanticColor = getSemanticSeriesColor(d.label);
      const color = semanticColor
        ? {
            border: semanticColor,
            background: withAlpha(semanticColor, 0.24),
            hover: withAlpha(semanticColor, 0.44),
          }
        : getAnalyticsSeriesColors(index, { variant: paletteVariant, label: d.label });
      data.datasets.push({
        label: d.label,
        data: d.data,
        borderColor: color.border,
        backgroundColor: color.background,
        borderWidth: 1,
        borderRadius: 3,
        hoverBackgroundColor: color.hover,
      });
    });
  } else {
    //code to be cleaned up next sprint, chart components need to be independent of data sources
    chartData.forEach((d, index) => {
      const datum = chartJSDataParser(d);
      const semanticColor = getSemanticSeriesColor(d.name);
      const color = semanticColor
        ? {
            border: semanticColor,
            background: withAlpha(semanticColor, 0.24),
            hover: withAlpha(semanticColor, 0.44),
          }
        : getAnalyticsSeriesColors(index, { variant: paletteVariant, label: d.name });
      const isValidDatum = d.name !== undefined && d.name !== 'All Team';

      if (isValidDatum) {
        data.datasets.push({
          label: d.name,
          data: datum,
          borderColor: color.border,
          backgroundColor: color.background,
          borderWidth: 1,
          borderRadius: 3,
          hoverBackgroundColor: color.hover,
        });
      }
    });
  }

  const options = {
    plugins: {
      legend: {
        position: 'right',
        labels: {
          filter: function (item) {
            // remove empty month placeholder from the legend
            return item.text !== placeholderName;
          },
        },
      },
      title: {
        display: showTitle,
        text: title,
      },
    },
    responsive: true,
    scales: {
      x: {
        stacked: true,
      },
      y: {
        stacked: true,
      },
    },
    maintainAspectRatio: false,
  };

  return <Bar options={options} data={data} />;
}

StackedBarChart.propTypes = {
  chartData: PropTypes.array,
  title: PropTypes.string,
  query: PropTypes.string,
  type: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showTitle: PropTypes.bool,
  paletteVariant: PropTypes.string,
};

function dateStrToMonth(timestamp) {
  const date = dayjs(timestamp);
  return date.format('MMM YYYY');
}

//parse data to fit chartJS data structure
function chartJSDataParser(d) {
  return d.x.reduce((accumulator, key, index) => {
    accumulator[dateStrToMonth(key)] = d.y[index];
    return accumulator;
  }, {});
}

//returns an object containing the start and end date according to the URL parameters in the query string
function parseDateRangeFromQuery(query) {
  const params = new URLSearchParams(query);
  const startDate = new Date(params.get('startDate'));
  startDate.setDate(1);
  const endDate = new Date(params.get('endDate'));
  return { startDate, endDate };
}

//returns an array of dates in the format 'Month Year'
function getMonthDatesArray(startDate, endDate) {
  let datesArray = [];

  while (startDate <= endDate) {
    let options = { month: monthStrLen, year: 'numeric' };
    let formattedDate = startDate.toLocaleDateString('en-US', options);
    datesArray.push(formattedDate);
    startDate.setMonth(startDate.getMonth() + 1);
  }
  return datesArray;
}

function getMonthsWithInitialValue1(monthDatesArray) {
  let result = monthDatesArray.reduce((acc, curr) => {
    acc[curr] = 0;
    return acc;
  }, {});
  return result;
}
